/** Which tab of a screen is showing, kept in the URL as `tab`.
 *
 * In the URL rather than in `useState` for the reason the record page and the inventory register
 * give: a tab is a thing somebody links to ("look at the Security tab") and a thing Back returns
 * to. `tab` is already in `search.ts`'s contract, so nothing needs adding there.
 *
 * **An unknown value falls back to the first tab, not to an empty panel.** The same parameter is
 * carried across screens by `keepSearch`, so `/organisation` can arrive holding the inventory's
 * `tab=register`; treating that as a tab nothing matches would render a blank body. The first tab
 * is "absent", and absent is written as no parameter at all, so there is one URL for the default
 * view rather than two.
 */

import { useNavigate, useSearch } from "@tanstack/react-router";

export interface UrlTabSpec<K extends string = string> {
  readonly key: K;
  readonly label: string;
}

export function useUrlTab<K extends string>(
  tabs: readonly UrlTabSpec<K>[],
): readonly [K, (key: string) => void] {
  const navigate = useNavigate();
  const search: { tab?: string } = useSearch({ strict: false });
  const first = tabs[0]?.key as K;
  const tab = tabs.find((entry) => entry.key === search.tab)?.key ?? first;
  const select = (key: string) => {
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        tab: key === first ? undefined : key,
      }),
    });
  };
  return [tab, select] as const;
}
